import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Grid,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { visuallyHidden } from '@mui/utils';

//icons
import { Edit } from '@mui/icons-material';
import PreviewIcon from '@mui/icons-material/Preview';

//components
import ReusableButton from '../../components/UI/Button';
import ReusableRoleModal from '../../components/UI/Modal';
import ReusableSelect from '../../components/UI/Select';
import Spinner from '../../components/UI/Spinner';

//services
import { GetUsers, AddLevel, GetMe, AddRoyalty } from '../../services/users';

const MARKETPLACE_URL = process.env.REACT_APP_MARKETPLACE_URL

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'left',
  color: theme.palette.text.secondary,
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'index',
    numeric: true,
    label: 'ID',
  },
  {
    id: 'first_name',
    label: 'NAME',
  },
  {
    id: 'avatar',
    label: 'AVATAR',
    disabled: true,
  },
  {
    id: 'wallet_address',
    label: 'WALLET ADDRESS',
    disabled: true,
  },
  {
    id: 'role',
    label: 'CHANGE ROLE',
    disabled: true,
  },
  {
    id: 'view',
    label: 'VIEW PROFILE',
    disabled: true,
  }
];
function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow >
        {headCells.map((headCell) => (
          <TableCell
            sx={{ fontWeight: 700, fontSize: 16 }}
            key={headCell.id}
            align='left'
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              disabled={headCell.disabled}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component='span' sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        mt: 5
      }}
    >
      <Typography
        sx={{ flex: '1 1 60%', fontWeight: 700, fontFamily: 'Montserrat', fontSize: 36, color: 'black' }}
        variant='h4'
        id='tableTitle'
        component='div'
      >
        Users
      </Typography>
    </Toolbar>
  );
};

const UserList = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('index');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openRole, setOpenRole] = useState(false);
  const [level, setLevel] = useState('');
  const [userId, setUserId] = useState();
  const [username, setUserName] = useState('');
  const [data, setData] = useState([]);
  const [royalty, setRoyalty] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    GetMe().then(() => {
      GetUsers().then(response => {
        addIndexToData(response);
      }).catch((err) => {
        setData([]);
      })
    }).catch((err) => {
      navigate(`/`);
    })
  }, [navigate]);

  const addIndexToData = (response) => {
    if (response.users === null) {
      setData([]);
      setPageLoading(false);
    } else {
      let i = 1;
      let userData = [];
      response.users.forEach(element => {
        element = { ...element, 'index': i };
        userData.push(element);
        i++;
      });
      setData(userData);
      setPageLoading(false);
    }
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //level
  const handleOpenRole = (id, name, royalty) => {
    setUserId(id);
    setUserName(name);
    setRoyalty(royalty);
    setOpenRole(true);
  }
  const handleCloseRole = () => {
    setOpenRole(false);
    setLevel('')
  }
  const handleLevel = (event) => {
    let value = event.target.value;
    let number = Number(value);
    setLevel(number);

  };
  const handleUpdateLevel = async () => {
    AddLevel(userId, level).then(() => {
      if (royalty === 0) {
        AddRoyalty(userId, 10).then(() => {
        }).catch(err => { })
      }
      GetUsers().then(response => {
        addIndexToData(response);
      }).catch((err) => {
        setData([]);
      })
      setOpenRole(false);
    })
  }
  let menus = [
    { key: 1, value: 50, name: 'Artist' }
  ]

  const handleClickView = (props) => {
    const id = props;
    window.open(`${MARKETPLACE_URL}/users/${id}`);
  }

  return (
    <Grid container spacing={2} justifyContent="flex-end">
      <Grid item xs={12} md={11}>
        {pageLoading ? <Spinner /> :
          <>
            <EnhancedTableToolbar />
            <Item elevation={0} variant="outlined" sx={{ mr: { md: 4, lg: 6, xl: 10 }, mt: 5, borderRadius: 3 }}>
              <TableContainer>
                <Table sx={{ minWidth: 750, mt: 5 }} aria-labelledby='tableTitle'>
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    rowCount={data.length}
                  />
                  <TableBody>
                    {stableSort(data.filter(artist => artist.level === 1), getComparator(order, orderBy))
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => {
                        const labelId = `enhanced-table-checkbox-${index}`;
                        return (
                          <TableRow tabIndex={-1} key={index} sx={{ fontFamily: 'Montserrat, sans-serif' }}>
                            <TableCell id={labelId} align='left'>
                              {row.index}
                            </TableCell>
                            <TableCell><Stack spacing={0}>
                              {row.first_name !== '' || row.last_name !== '' ? (
                                <div style={{ fontWeight: 600, fontSize: 16 }}>{row.first_name + ' ' + row.last_name}</div>
                              ) : (
                                <div style={{ fontWeight: 600, fontSize: 16 }}>-</div>
                              )}
                              <div style={{ fontWeight: 600, fontSize: 16, color: '#919191' }}>{row.Email.address}</div>
                            </Stack></TableCell>
                            <TableCell align='right'>
                              <Avatar alt='avatar' src={row.avatar_url} />
                            </TableCell>
                            <TableCell align='left' sx={{ fontSize: 15 }}>{
                              <>
                                {row.WalletList !== null ? (
                                  row.WalletList.map((wallet) => {
                                    return wallet.address + "\n"
                                  })
                                ) : ("-")
                                }
                              </>
                            }</TableCell>
                            <TableCell align='left'>
                              <ReusableButton endIcon={<Edit />} sx={{
                                backgroundColor: '#e8874f',
                                '&.MuiButtonBase-root:hover': {
                                  backgroundColor: '#eda57b',
                                },
                              }} onClick={() => handleOpenRole(row.id, row.username, row.royalities)}>{row.level === 1 && 'Buyer'} </ReusableButton>
                            </TableCell>
                            <TableCell align='left'>
                              <ReusableButton onClick={() => handleClickView(row.id)} sx={{ backgroundColor: '#263af7' }} endIcon={<PreviewIcon />}>
                                View
                              </ReusableButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Item>
          </>
        }
      </Grid>
      <ReusableRoleModal open={openRole} onClose={handleCloseRole}>
        <Box
          sx={{ mb: 3 }}>
          <div style={{ fontWeight: 600, marginRight: 5 }}>{username}</div>
        </Box>
        <ReusableSelect title={'Role'} value={level} onChange={handleLevel} menus={menus} />
        <ReusableButton sx={
          {
            border: '1px solid #263af7',
            color: '#263af7',
            background: 'none',
            '&.MuiButtonBase-root:hover': {
              background: 'none',
              border: '1px solid #263af7',
            },
            marginRight: 2,
            marginTop: 3
          }
        } onClick={handleCloseRole} type='submit'>Cancel</ReusableButton>
        <ReusableButton sx={
          {
            backgroundColor: '#263AF7',
            '&.MuiButtonBase-root:hover': {
              backgroundColor: '#263AF7',
            },
            marginTop: 3
          }
        } disabled={level === ''} onClick={handleUpdateLevel} type='submit'>Update</ReusableButton>
      </ReusableRoleModal>
    </Grid>
  );
};

export default UserList;
